import { useCallback } from 'react';
import { Team } from 'api/models';
import {
  FetchableResourceType,
  ResourceType,
  TeamIdentifier,
  UserIdentifier,
} from 'api/resource-management/types';
import {
  GroupBy,
  ProjectsWithMembers,
} from 'components/organisms/resource-management/types';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useUser } from 'hooks/use-user';
import { logger } from 'services';
import { filterAllocationEvents } from '../utils/events/events';
import { useAggregationManager } from './aggregation-manager';
import { useAllocationAndSummaryManager } from './resource-summary-manager';
import { ManagerPropsWithLoadedRange } from './types';
import { useTotalAllocationInUIManager } from './ui-total-allocation-manager';

interface Result {
  refreshAllocationForProject: (
    project: ProjectsWithMembers,
    teams: Team[],
  ) => void;
}

export const useRefreshAllocationsManager = (
  props: ManagerPropsWithLoadedRange,
): Result => {
  const { schedulerRef, rangeLoadedInUI, groupBy } = props;
  const { getAllocatedMinutesFromAggregation, removeAggregationForResource } =
    useAggregationManager();
  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const isCollaborator = useIsCurrentUserCollaborator();
  const { setTotalAllocationForResource, setTotalAllocationForProject } =
    useTotalAllocationInUIManager(props);
  const {
    user: { userId: currentUserId },
  } = useUser();

  const refreshAllocationForProject = useCallback(
    async (project: ProjectsWithMembers, teams: Team[]) => {
      try {
        schedulerRef.current.instance.suspendRefresh();

        const allEvents = schedulerRef.current.instance.eventStore.allRecords;
        const oldProjectSpecificEvents = filterAllocationEvents(allEvents, {
          projectId: project.projectId,
        });

        oldProjectSpecificEvents.forEach((event) => {
          schedulerRef.current.instance.eventStore.remove(event);
        });
        const userResourceIdentifiers: UserIdentifier[] = project.projectMembers
          .filter(({ user: { userId } }) =>
            isCollaborator ? userId === currentUserId : true,
          )
          .map((projectUser) => ({
            type: ResourceType.User,
            userId: projectUser.user.userId,
            roleId: projectUser.role?.roleId,
            referenceTeamId: projectUser.referenceTeam?.teamId,
          }));
        const teamResourceIdentifiers: TeamIdentifier[] = isCollaborator
          ? []
          : teams.map(({ teamId }) => ({
              type: ResourceType.Team,
              teamId,
            }));

        userResourceIdentifiers.forEach((userResourceIdentifier) => {
          removeAggregationForResource(
            userResourceIdentifier,
            project.projectId,
          );
        });
        teamResourceIdentifiers.forEach((teamResourceIdentifier) => {
          removeAggregationForResource(
            teamResourceIdentifier,
            project.projectId,
          );
        });
        const allocations = await updateAllocationAndSummary(
          {
            [FetchableResourceType.ProjectList]: [project.projectId],
            ...(isCollaborator
              ? {
                  [FetchableResourceType.UserList]: [currentUserId],
                }
              : {}),
          },
          rangeLoadedInUI,
        );

        if (groupBy === GroupBy.Projects) {
          const totalMinutesForProject = getAllocatedMinutesFromAggregation(
            project.projectId,
          ).totalAllocatedMinutes;

          setTotalAllocationForProject(
            project.projectId,
            totalMinutesForProject,
          );
        }

        userResourceIdentifiers.forEach((userIdentifier) => {
          const totalMinutes = getAllocatedMinutesFromAggregation(
            project.projectId,
            userIdentifier,
          ).totalAllocatedMinutes;

          setTotalAllocationForResource(
            project.projectId,
            userIdentifier,
            totalMinutes,
          );
        });
        teamResourceIdentifiers.forEach((teamIdentifier) => {
          const totalMinutes = getAllocatedMinutesFromAggregation(
            project.projectId,
            teamIdentifier,
          ).totalAllocatedMinutes;

          setTotalAllocationForResource(
            project.projectId,
            teamIdentifier,
            totalMinutes,
          );
        });
        allocations.forEach((allocation) => {
          allocation.aggregations.forEach((aggregation) => {
            if (
              aggregation.type === ResourceType.User &&
              aggregation.projectId === project.projectId &&
              (!isCollaborator || aggregation.userId === currentUserId)
            ) {
              const userIdentifier: UserIdentifier = {
                type: ResourceType.User,
                userId: aggregation.userId,
                ...(aggregation.referenceTeamId != null
                  ? {
                      roleId: aggregation.roleId,
                      referenceTeamId: aggregation.referenceTeamId,
                    }
                  : {}),
              };
              const totalMinutes = getAllocatedMinutesFromAggregation(
                project.projectId,
                userIdentifier,
              ).totalAllocatedMinutes;

              setTotalAllocationForResource(
                project.projectId,
                userIdentifier,
                totalMinutes,
              );
            }
            if (
              aggregation.type === ResourceType.Team &&
              aggregation.projectId === project.projectId &&
              !isCollaborator
            ) {
              const teamIdentifier: TeamIdentifier = {
                type: ResourceType.Team,
                teamId: aggregation.teamId,
              };
              const totalMinutes = getAllocatedMinutesFromAggregation(
                project.projectId,
                teamIdentifier,
              ).totalAllocatedMinutes;

              setTotalAllocationForResource(
                project.projectId,
                teamIdentifier,
                totalMinutes,
              );
            }
          });
        });

        schedulerRef.current.instance.resumeRefresh(true);
      } catch (e) {
        logger.error('error in refreshing the allocation', { error: e });
      }
    },
    [
      currentUserId,
      getAllocatedMinutesFromAggregation,
      groupBy,
      isCollaborator,
      rangeLoadedInUI,
      removeAggregationForResource,
      schedulerRef,
      setTotalAllocationForProject,
      setTotalAllocationForResource,
      updateAllocationAndSummary,
    ],
  );

  return {
    refreshAllocationForProject,
  };
};
